import React from "react";

export interface ElementDimensions<T extends HTMLElement> {
    ref: React.Ref<T>;
    dimensions: DimensionModel;
    calcDimensions(): void;
}

export interface DimensionModel {
    width: number;
    height: number;
    innerWidth: number;
    innerHeight: number;

    minWidth(minWidth: number): boolean;
    maxWidth(maxWidth: number): boolean;
}

const makeDimension = ({
    width,
    height,
    innerWidth,
    innerHeight,
}: {
    width: number;
    height: number;
    innerWidth: number;
    innerHeight: number;
}): DimensionModel => ({
    width,
    height,
    innerWidth,
    innerHeight,
    minWidth: (min) => width >= min,
    maxWidth: (max) => width < max,
});

export interface UseElementDimensionsOptions {
    /** Measure synchronously instead of deferring to requestAnimationFrame. */
    sync?: boolean;
    /** Observe element size changes via ResizeObserver. */
    observeResize?: boolean;
}

export function useElementDimensions<T extends HTMLElement>(
    initialElement?: T,
    options?: UseElementDimensionsOptions,
): ElementDimensions<T> {
    const { sync = false, observeResize = false } = options ?? {};
    const [dimensions, setDimensions] = React.useState(
        makeDimension({ width: 0, height: 0, innerWidth: 0, innerHeight: 0 }),
    );
    const [element, setElement] = React.useState(initialElement);
    const ref: React.Ref<T> = React.useCallback((instance: T) => setElement(instance), []);
    const calcDimensions = React.useCallback(() => {
        const measure = () => {
            if (!element) {
                return;
            }

            const width = element.offsetWidth;
            const height = element.offsetHeight;

            const cs = window.getComputedStyle(element);
            const hpadding = parseInt(cs.paddingLeft, 10) + parseInt(cs.paddingRight, 10);
            const vpadding = parseInt(cs.paddingTop, 10) + parseInt(cs.paddingBottom, 10);
            const innerWidth = width - hpadding;
            const innerHeight = height - vpadding;

            setDimensions(makeDimension({ width, height, innerWidth, innerHeight }));
        };

        if (sync) {
            measure();
        } else {
            window.requestAnimationFrame(measure);
        }
    }, [element, sync]);

    React.useLayoutEffect(() => {
        const observer = new MutationObserver((mutations) => {
            if (
                mutations.some(
                    (mutation) =>
                        mutation.type === "attributes" && mutation.attributeName === "style",
                )
            ) {
                calcDimensions();
            }
        });

        const resizeObserver =
            observeResize && typeof ResizeObserver !== "undefined" && element
                ? new ResizeObserver(() => calcDimensions())
                : undefined;

        window.addEventListener("resize", calcDimensions);
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", calcDimensions);
        }
        window.addEventListener("orientationchange", calcDimensions);
        if (element) {
            observer.observe(element, { attributes: true });
            resizeObserver?.observe(element);
        }
        calcDimensions();

        return () => {
            window.removeEventListener("resize", calcDimensions);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", calcDimensions);
            }
            window.removeEventListener("orientationchange", calcDimensions);
            observer.disconnect();
            resizeObserver?.disconnect();
        };
    }, [element, calcDimensions, observeResize]);

    return { ref, dimensions, calcDimensions };
}
